import { isValidElement, type ReactNode } from "react";

// Pure transform from antd Tree `treeData` to MUI RichTreeView `items`.
// MUI-free so it's unit-tested without mounting the tree. Types are widened to
// structurally accept antd's DataNode (title: ReactNode, key: Key, extra fields).

// MUI RichTreeView v8 requires a STRING label (keyboard-nav does label.toLowerCase()),
// so a JSX/fragment title crashes the whole tree. Flatten a ReactNode to its text so
// the node still renders + is nav-able. (Decorative icons in the label are dropped;
// preserving them would need a custom RichTreeView item slot.)
export const nodeToText = (node: ReactNode): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (isValidElement(node)) return nodeToText((node.props as { children?: ReactNode }).children);
  return "";
};

export interface AntdTreeNode {
  // antd DataNode allows a render-function title; support both.
  title?: ReactNode | ((data: AntdTreeNode) => ReactNode);
  key: string | number;
  children?: AntdTreeNode[];
  disabled?: boolean;
  // antd DataNode carries extra optional fields (icon, selectable, ...); allow them.
  [k: string]: unknown;
}

export interface MuiTreeItem {
  id: string;
  label: ReactNode;
  children?: MuiTreeItem[];
}

export const toTreeItems = (nodes: AntdTreeNode[]): MuiTreeItem[] =>
  nodes.map((n) => {
    const title = typeof n.title === "function" ? n.title(n) : n.title;
    // string labels pass through; non-string (JSX) are flattened to text so MUI
    // RichTreeView doesn't crash on label.toLowerCase() (renders zero items).
    const label = typeof title === "string" ? title : nodeToText(title) || String(n.key);
    return {
      id: String(n.key),
      label,
      ...(Array.isArray(n.children) && n.children.length ? { children: toTreeItems(n.children) } : {}),
    };
  });

/** All keys depth-first — for expand-all / select-all parity helpers. */
export const collectKeys = (nodes: AntdTreeNode[]): string[] =>
  nodes.flatMap((n) => [String(n.key), ...(Array.isArray(n.children) ? collectKeys(n.children) : [])]);

/** key -> parent key map for the tree (roots absent). */
const parentMap = (nodes: AntdTreeNode[]): Map<string, string> => {
  const parent = new Map<string, string>();
  const walk = (ns: AntdTreeNode[], p?: string) => {
    ns.forEach((n) => {
      const k = String(n.key);
      if (p !== undefined) parent.set(k, p);
      if (Array.isArray(n.children)) walk(n.children, k);
    });
  };
  walk(nodes);
  return parent;
};

/** antd `autoExpandParent` parity: return the given keys plus all their ancestor
 *  keys. MUI RichTreeView only shows a node when every ancestor is expanded, so
 *  expanding a deep node requires its ancestors be expanded too (antd did this
 *  implicitly when autoExpandParent was set). */
export const withAncestorKeys = (nodes: AntdTreeNode[], keys: string[]): string[] => {
  const parent = parentMap(nodes);
  const out = new Set<string>();
  keys.forEach((k) => {
    out.add(k);
    let cur = parent.get(k);
    while (cur !== undefined && !out.has(cur)) {
      out.add(cur);
      cur = parent.get(cur);
    }
  });
  return [...out];
};

/** Keep only keys whose entire ancestor chain is also expanded. antd unmounts a
 *  node when any ancestor is collapsed; MUI keeps an "orphaned" expanded node
 *  mounted, so we drop those to match antd (collapsing a parent hides its subtree). */
export const pruneOrphanKeys = (nodes: AntdTreeNode[], keys: string[]): string[] => {
  const parent = parentMap(nodes);
  const set = new Set(keys);
  return keys.filter((k) => {
    let cur = parent.get(k);
    while (cur !== undefined) {
      if (!set.has(cur)) return false;
      cur = parent.get(cur);
    }
    return true;
  });
};

const findNode = (nodes: AntdTreeNode[], key: string): AntdTreeNode | undefined => {
  for (const n of nodes) {
    if (String(n.key) === key) return n;
    if (Array.isArray(n.children)) {
      const found = findNode(n.children, key);
      if (found) return found;
    }
  }
  return undefined;
};

/** Expand a (possibly minimal/compressed) checked set to its full closure: for
 *  every checked key, include the key and ALL its descendants. Filter consumers
 *  store only the top-most fully-checked key (a "min prefix" e.g. just "Indexes"),
 *  but MUI RichTreeView renders a checkbox as checked only if its own id is in
 *  selectedItems — so without expanding, every child renders UNCHECKED (the bug:
 *  clicking a parent/child showed no descendant selection). */
export const expandCheckedClosure = (
  nodes: AntdTreeNode[],
  keys: Array<string | number>,
): string[] => {
  const out = new Set<string>();
  const addSubtree = (n: AntdTreeNode) => {
    out.add(String(n.key));
    if (Array.isArray(n.children)) n.children.forEach(addSubtree);
  };
  keys.map(String).forEach((k) => {
    out.add(k);
    const node = findNode(nodes, k);
    if (node && Array.isArray(node.children)) node.children.forEach(addSubtree);
  });
  return [...out];
};

/** antd `checkable` cascade: MUI RichTreeView (Community, v8) doesn't propagate a
 *  parent check to its descendants. Given the previous checked set and MUI's new
 *  set, propagate each toggled node's check/uncheck down to all descendants. */
export const propagateChecks = (
  nodes: AntdTreeNode[],
  prev: Array<string | number>,
  next: Array<string | number>,
): string[] => {
  const prevSet = new Set(prev.map(String));
  const nextSet = new Set(next.map(String));
  const result = new Set([...nextSet]);
  const eachDescendant = (key: string, apply: (k: string) => void) => {
    const node = findNode(nodes, key);
    if (!Array.isArray(node?.children)) return;
    const walk = (ns: AntdTreeNode[]) =>
      ns.forEach((n) => {
        apply(String(n.key));
        if (Array.isArray(n.children)) walk(n.children);
      });
    walk(node!.children);
  };
  [...nextSet].filter((k) => !prevSet.has(k)).forEach((k) => eachDescendant(k, (d) => result.add(d)));
  [...prevSet].filter((k) => !nextSet.has(k)).forEach((k) => eachDescendant(k, (d) => result.delete(d)));
  return [...result];
};
